import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Chip,
  Box,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EuroIcon from "@mui/icons-material/Euro";
import CategoryIcon from "@mui/icons-material/Category";
import CommuteIcon from "@mui/icons-material/Commute";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTranslation } from "react-i18next";
import Calendar from "./Calendar/Calendar";
import LockPerson from "@mui/icons-material/LockPerson";
import axios from "../../../../../../Utils/axios";
import LocalStorage from "../../../../../../Utils/localStorageService";
import { toast } from "react-toastify";

const ModernDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "20px",
    padding: theme.spacing(1),
    boxShadow: "0px 20px 50px rgba(0,0,0,0.1)",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(3),
    backgroundColor: "#fcfcfc",
  },
}));

export default function InfosModal(props) {
  const [openCalendar, setOpenCalendar] = React.useState(false);
  const [allDates, setAllDates] = React.useState([]);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  const [clientData, setClientData] = React.useState(null);
  const [loadingClient, setLoadingClient] = React.useState(false);
  const [loginModal, setLoginModal] = React.useState(false);
  const { t } = useTranslation();

  const showAddress =
    props.offre?.format_offre === "presentiel" ||
    props.offre?.format_offre === "hybride";

  const fetchAvailabilities = React.useCallback(() => {
    const slots = props.offre?.available_slots;
    if (slots?.length > 0) {
      const sorted = [...slots].sort((a, b) => new Date(a) - new Date(b));
      setAllDates([...new Set(sorted.map((s) => s.split(" ")[0]))]);
      setStartDate(sorted[0]);
      setEndDate(sorted[sorted.length - 1]);
    }
  }, [props.offre]);

  React.useEffect(() => {
    fetchAvailabilities();
  }, [fetchAvailabilities]);

  if (!props.offre) return null;

  /**
   * =====================================================
   * 🔥 CHECK CLIENT AVANT RESERVATION
   * =====================================================
   */
  const handleOpenReservation = async () => {
    if (loadingClient) return;

    try {
      setLoadingClient(true);

      const token = LocalStorage.getAccessToken();
      // ✅ 1. check login FIRST (rapide, sans API)
      if (!token) {
        setLoginModal(true);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_APIURL}/client/g/get/p/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.status !== "success") {
        throw new Error("Client invalide");
      }

      const client = response.data.result;

      const hasCredit =
        (client?.nbr_offre ?? 0) > 0 || (client?.tokens ?? 0) > 0;

      setClientData({
        ...client,
        hasCredit,
      });

      setOpenCalendar(true);
      props.setOpenInfosModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Impossible de charger le client");
    } finally {
      setLoadingClient(false);
    }
  };

  return (
    <>
      <Calendar
        isOpen={openCalendar}
        setOpenCalendar={setOpenCalendar}
        offre={props.offre}
        availableDates={allDates}
        startDate={startDate}
        endDate={endDate}
        clientData={clientData}
      />

      <ModernDialog
        onClose={() => props.setOpenInfosModal(false)}
        open={props.isOpen}
        maxWidth="md"
        fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h5" fontWeight="800" color="#2D3748">
              {t("infosModal.reserveThisOffer")}
            </Typography>
            <Typography variant="h5" fontWeight="800" color="#4AAE9B">
              {props.offre.titre_offre}
            </Typography>
          </Stack>

          <IconButton
            onClick={() => props.setOpenInfosModal(false)}
            sx={{ position: "absolute", right: 16, top: 16 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={4}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  textTransform: "uppercase",
                  color: "#A0AEC0",
                  fontWeight: "700",
                  mb: 1,
                }}>
                {t("infosModal.description")}
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: "#4A5568", lineHeight: 1.7 }}>
                {props.offre.description_offre}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}>
              <InfoTile
                icon={<AccessTimeIcon />}
                label={t("infosModal.duration")}
                value={`${props.offre.duree_offre} min`}
              />
              <InfoTile
                icon={<EuroIcon />}
                label={t("infosModal.price")}
                value={`${props.offre.prix_offre}€`}
              />
              <InfoTile
                icon={<CategoryIcon />}
                label={t("infosModal.type")}
                value={props.offre.type_offre?.replace("_", " ")}
              />

              {props.offre.is_deplacement_possible === 1 && (
                <InfoTile
                  icon={<CommuteIcon />}
                  label="Mobilité"
                  value="Déplacement possible"
                  highlight
                />
              )}
            </Box>

            {showAddress && props.offre.address_ws && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: "15px",
                  backgroundColor: "#f8fafc",
                  borderLeft: "4px solid #4AAE9B",
                }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <LocationOnIcon sx={{ color: "#4AAE9B" }} />
                  <Typography variant="subtitle1" fontWeight="800">
                    Lieu du rendez-vous
                  </Typography>
                </Stack>

                <Typography variant="body2" sx={{ color: "#4A5568", ml: 4 }}>
                  <strong>{props.offre.address_ws}</strong>
                  <br />
                  {props.offre.postal_code_ws} {props.offre.city_ws},{" "}
                  {props.offre.country_ws}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Chip
            label={
              props.offre.is_accessible_participant === 1
                ? t("infosModal.accessibleWithTokens")
                : t("infosModal.payDirectly")
            }
          />

          <Button
            onClick={handleOpenReservation}
            disabled={loadingClient}
            variant="contained"
            startIcon={<CalendarMonthIcon />}
            sx={{
              backgroundColor: "#4AAE9B",
              borderRadius: "12px",
              px: 4,
              py: 1.2,
              fontWeight: "800",
              textTransform: "none",
              "&:hover": { backgroundColor: "#3d9685" },
            }}>
            {loadingClient ? "Chargement..." : t("infosModal.reserve")}
          </Button>
        </DialogActions>
      </ModernDialog>
      <Dialog
  open={loginModal}
  onClose={() => setLoginModal(false)}
  PaperProps={{
    sx: {
      borderRadius: "20px",
      p: 3,
      maxWidth: "420px",
    },
  }}
>
  <DialogContent>
    <Stack alignItems="center" spacing={2}>
      <LockPerson sx={{ fontSize: 60, color: "#4AAE9B" }} />

      <Typography variant="h6" fontWeight={800}>
        Connexion requise
      </Typography>

      <Typography textAlign="center" sx={{ color: "#6B7280" }}>
        Vous devez être connecté pour réserver cette offre.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        onClick={() => (window.location.href = "/login")}
        sx={{
          backgroundColor: "#4AAE9B",
          borderRadius: "12px",
          py: 1.5,
          fontWeight: 700,
        }}
      >
        Se connecter
      </Button>

      <Button
        onClick={() => setLoginModal(false)}
        sx={{ color: "#9CA3AF" }}
      >
        Annuler
      </Button>
    </Stack>
  </DialogContent>
</Dialog>
    </>
  );
}

/**
 * =====================================================
 * TILE UI
 * =====================================================
 */
function InfoTile({ icon, label, value, highlight }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        p: 2,
        borderRadius: "12px",
        border: "1px solid #edf2f7",
        backgroundColor: highlight ? "#f0fdfa" : "transparent",
      }}>
      <Box sx={{ color: "#4AAE9B" }}>{icon}</Box>

      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>

        <Typography variant="body2" fontWeight="700">
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}
